import { useMutation, useQueryClient } from '@tanstack/react-query';
import ContractTemplateService, {
  IUpdateContractTemplateParams,
} from '@clickersphere/core/services/ContractTemplateService';
import { useBuildQueryKey } from '../useBuildQueryKey';
import {
  ContractTemplateQueryData,
  CONTRACT_TEMPLATES_QUERY_KEY,
} from './useContractTemplates';

export function useUpdateContractTemplate() {
  const queryClient = useQueryClient();
  const { buildQueryKey } = useBuildQueryKey();

  const {
    isPending: updateContractTemplateIsPending,
    mutateAsync: updateContractTemplateAsync,
  } = useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string;
      data: IUpdateContractTemplateParams;
    }) => ContractTemplateService.update(id, data),
    onMutate: async (variables) => {
      await queryClient.cancelQueries({
        queryKey: buildQueryKey(CONTRACT_TEMPLATES_QUERY_KEY),
      });

      const oldContractTemplates = queryClient.getQueryData<
        ContractTemplateQueryData[]
      >(buildQueryKey(CONTRACT_TEMPLATES_QUERY_KEY));

      queryClient.setQueryData<ContractTemplateQueryData[]>(
        buildQueryKey(CONTRACT_TEMPLATES_QUERY_KEY),
        (old) =>
          old?.map((item) =>
            item.id === variables.id
              ? { ...item, ...variables.data, queryStatus: 'pending' }
              : item,
          ),
      );

      return { oldContractTemplates };
    },
    onSuccess: async (_data, variables) => {
      await queryClient.cancelQueries({
        queryKey: buildQueryKey(CONTRACT_TEMPLATES_QUERY_KEY),
      });

      queryClient.setQueryData<ContractTemplateQueryData[]>(
        buildQueryKey(CONTRACT_TEMPLATES_QUERY_KEY),
        (old) =>
          old?.map((item) =>
            item.id === variables.id
              ? { ...item, queryStatus: undefined }
              : item,
          ),
      );
    },
    onError: async (_error, _variables, context) => {
      await queryClient.cancelQueries({
        queryKey: buildQueryKey(CONTRACT_TEMPLATES_QUERY_KEY),
      });

      queryClient.setQueryData(
        buildQueryKey(CONTRACT_TEMPLATES_QUERY_KEY),
        context?.oldContractTemplates,
      );
    },
  });

  return {
    updateContractTemplateAsync,
    updateContractTemplateIsPending,
  };
}
